/**
 * What is asked for when a crew is created.
 *
 * Jonathon, 2026-09-14: "I want it to be a pop up where we enter all of the
 * crew's info and then submit it." Before this, Create made a row called "New
 * crew" and left somebody to fill the line in cell by cell — which is also how
 * a half-finished crew ended up in the list, named nothing, blocking the next
 * one.
 *
 * The draft lives here rather than in the component (rule 5) because two of the
 * three rules it carries are ones the database would otherwise enforce with an
 * error nobody can act on:
 *
 *  - a crew has to be called something;
 *  - two crews cannot share a name (`crews_name_key`), and the error Postgres
 *    gives for that names the constraint rather than the crew in the way;
 *  - a name is stored trimmed, so " Elvis " and "Elvis" are the same crew and
 *    the second one is refused rather than created.
 */

/** A crew as the form holds it, before anything is written. */
export interface CrewDraft {
  /** The company. For a one-man crew, the man. */
  name: string;
  /** Who to call there. Shown in brackets after the company, see `crewLabel`. */
  contact_name: string;
  phone: string;
  email: string;
  /** Working for us today. A crew created switched-off would be odd. */
  active: boolean;
  /** Signs in to the app to see their own work orders. */
  can_login: boolean;
  /** Our own employees — costed, never paid out through QuickBooks. */
  is_in_house: boolean;
  /** Trade ids, as `crew_trades` holds them. */
  tradeIds: string[];
}

export const blankCrewDraft = (): CrewDraft => ({
  name: "",
  contact_name: "",
  phone: "",
  email: "",
  active: true,
  can_login: false,
  is_in_house: false,
  tradeIds: [],
});

/**
 * Why this crew cannot be created yet, in words the office can act on.
 *
 * `existing` is every crew name in the table, active or not: an inactive crew
 * holds its name just as firmly as a working one, and one sixty rows down is
 * exactly the collision nobody can see coming.
 */
export function crewDraftProblem(
  draft: CrewDraft,
  existing: Iterable<string | null | undefined>,
): string | null {
  const name = draft.name.trim();
  if (name === "") return "Give the crew a name.";

  for (const taken of existing) {
    if (taken && taken.trim().toLowerCase() === name.toLowerCase()) {
      return `There is already a crew called ${taken.trim()}.`;
    }
  }

  return null;
}

/**
 * The row to insert, once there is no problem with it.
 *
 * Empty boxes become null rather than "": a crew with an email of "" looks like
 * a crew with an email everywhere that checks.
 */
export function crewDraftRow(draft: CrewDraft): {
  name: string;
  contact_name: string | null;
  phone: string | null;
  email: string | null;
  active: boolean;
  can_login: boolean;
  is_in_house: boolean;
} {
  const tidy = (value: string): string | null => value.trim() || null;
  return {
    name: draft.name.trim(),
    contact_name: tidy(draft.contact_name),
    phone: tidy(draft.phone),
    email: tidy(draft.email),
    active: draft.active,
    can_login: draft.can_login,
    is_in_house: draft.is_in_house,
  };
}
